import {Identity, Authenticated} from './model/identity';
import {HttpClientManager} from '../wc-http-client/index';

const participantCookieName: string = 'acting-participant';

export class AuthorisationManager {
  static getDomain(): string {
    let splitDomain = location.hostname.split('.');
    if (splitDomain.length > 1) splitDomain.shift();
    return splitDomain.join('.');
  }

  static setParticipant(participantId: string, participantName: string) {
    let participantJson = JSON.stringify({
      participantId: participantId,
      participantName: participantName
    });
    const domain = location.hostname !== 'localhost' ? `domain=${this.getDomain()};` : '';
    document.cookie = `${participantCookieName}=${participantJson}; ${domain} path=/`;
  }

  static getParticipant(): string {
    let participantCookie = this.getCookie(participantCookieName);
    if (participantCookie !== undefined && participantCookie !== '') {
      return JSON.parse(participantCookie).participantId;
    }
    return '';
  }

  static getParticipantName(): string {
    let participantCookie = this.getCookie(participantCookieName);
    if (participantCookie !== undefined && participantCookie !== '') {
      return JSON.parse(participantCookie).participantName;
    }
    return '';
  }

  // Get user participant ids by market (GAS,GSH)
  static getMarketParticipantIds(marketId: string): Array<string> {
    let tokenString = localStorage.getItem('aemo-session');
    if (tokenString !== undefined && tokenString !== '') {
      let token = JSON.parse(tokenString);
      if (token.MarketParticipantIds && token.MarketParticipantIds.length > 0) {
        let marketParticipants = token.MarketParticipantIds.filter(participant => participant.MarketId === marketId);
        if (marketParticipants.length > 0) {
          return marketParticipants.map(participant => participant.MarketParticipantId);
        } 
      }
    }
    return [];
  }


  static getCookie(cookieName: string): string | undefined {
    let value = "; " + document.cookie;
    let parts = value.split(`; ${cookieName}=`);
    if (parts.length == 2) {
      let parts2 = parts.pop();
      if (parts2 !== undefined)
        return parts2.split(";").shift();
    }

    return '';
  }

  static removeParticipant() {
    document.cookie = `${participantCookieName}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; domain=${this.getDomain()}; path=/`;
  }

  /**
    * returns TRUE if the authenticated user has the required permission and access operations for the given participant.
    * @param permission The id of the permission required
    * @param accessOperation The access operations required
    * @param participantId The id of the participant. If omitted, uses primary or set participant
    * @param identity The users Identity. If omitted, Identity will be retreived from the auth service
    */
  static async hasPermission(permission: string, accessOperation: AccessOperation, participantId?: string, identity?: Identity): Promise<boolean> {
    if (identity == null) {
      identity = await this.SessionData();
    }
    if (!identity) return false;

    if (participantId == null) {
      participantId = this.getParticipant();
      // AEMO users have access to all participants - replace participant ID
      if (identity.PrimaryParticipantId === 'NEMMCO') participantId = 'NEMMCO';
    }
    if (!participantId) return false;

    let searchString = `${permission}|${participantId}|`;
    let permissionString = identity.Permissions.find(x => x.startsWith(searchString));

    if (permissionString) {
      if (accessOperation === AccessOperation.any) return true;

      let accessOperationsString = permissionString.substring(searchString.length);
      if (!accessOperationsString) return false;

      if (accessOperation === AccessOperation.read && accessOperationsString.indexOf('R') == -1) return false;
      if (accessOperation === AccessOperation.create && accessOperationsString.indexOf('C') == -1) return false;
      if (accessOperation === AccessOperation.update && accessOperationsString.indexOf('U') == -1) return false;
      if (accessOperation === AccessOperation.delete && accessOperationsString.indexOf('D') == -1) return false;

      return true;
    }

    return false;
  }


  static async SessionData(): Promise<Identity> {
    let http = HttpClientManager.httpClientWithInterceptors();
    const response = await http.fetch('/api/auth/SessionData');
    return await response.json();
  }

  static async IsAuthenticated(): Promise<boolean> {
    let http = HttpClientManager.httpClientWithInterceptors();
    const response = await http.fetch('/api/auth/IsAuthenticated');
    let authenticated: Authenticated = await response.json();
    return authenticated.IsAuthenticated;
  }
}

/**
 * Identity access operations
 */
export enum AccessOperation {
  any,
  create,
  delete,
  read,
  update
};



// WEBPACK FOOTER //
// node_modules/wc-lib/src/wc-authorisation/authorisation.ts